import { demoChallenges, demoEvent, demoLeaderboard, demoLeagues, demoPredictions, demoProfile } from '../data/demo'
import { supabase } from './supabase'
import type {
  ChallengeDTO,
  EventDTO,
  LeaderboardEntry,
  LeagueDTO,
  PredictionDTO,
  PredictionInput,
  PublicProfileDTO
} from '../types/domain'

function mapFighter(row: any) {
  return {
    id: row.id,
    slug: row.slug,
    name: row.display_name ?? row.name,
    nickname: row.nickname,
    countryCode: row.country_code ?? 'ZZ',
    countryName: row.country_name ?? 'Não informado',
    weightClass: row.weight_class?.name ?? row.weight_class_name ?? 'Peso casado',
    rank: row.current_rank,
    titleStatus: row.title_status,
    imageUrl: row.image_url
  }
}

function mapFightResult(row: any) {
  if (!row) return null
  return {
    fightId: row.fight_id,
    winnerId: row.winner_id,
    method: row.method,
    round: row.round,
    time: row.time,
    official: row.official,
    version: row.version
  }
}

export async function fetchUpcomingEvent(): Promise<EventDTO> {
  if (!supabase) return demoEvent

  const { data: event, error } = await supabase
    .from('events')
    .select('*')
    .in('status', ['published', 'live'])
    .gte('start_at', new Date(Date.now() - 12 * 60 * 60_000).toISOString())
    .order('start_at', { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  if (!event) throw new Error('Nenhum evento publicado no momento.')

  const { data: fights, error: fightsError } = await supabase
    .from('fights')
    .select('*, red:red_fighter_id(*, weight_class:weight_classes(*)), blue:blue_fighter_id(*, weight_class:weight_classes(*)), result:fight_results(*)')
    .eq('event_id', event.id)
    .order('segment')
    .order('position')

  if (fightsError) throw fightsError

  return {
    id: event.id,
    slug: event.slug,
    name: event.name,
    headline: event.headline,
    venue: event.venue,
    city: event.city,
    countryCode: event.country_code,
    startAt: event.start_at,
    lockAt: event.lock_at,
    status: event.status,
    sourceUrl: event.source_url,
    fights: (fights ?? []).map((fight: any) => ({
      id: fight.id,
      eventId: fight.event_id,
      segment: fight.segment,
      position: fight.position,
      scheduledRounds: fight.scheduled_rounds,
      weightClass: fight.weight_class_name,
      isTitleFight: fight.is_title_fight,
      red: mapFighter(fight.red),
      blue: mapFighter(fight.blue),
      status: fight.status,
      result: mapFightResult(Array.isArray(fight.result) ? fight.result[0] : fight.result)
    }))
  }
}

export async function fetchMyPredictions(userId?: string): Promise<PredictionDTO[]> {
  if (!supabase || !userId) return demoPredictions
  const { data, error } = await supabase.from('predictions').select('*').eq('user_id', userId)
  if (error) throw error
  return (data ?? []).map((row: any) => ({
    id: row.id,
    userId: row.user_id,
    fightId: row.fight_id,
    winnerId: row.winner_id,
    method: row.method,
    round: row.round,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    points: row.points,
    winnerCorrect: row.winner_correct,
    methodCorrect: row.method_correct,
    roundCorrect: row.round_correct
  }))
}

export async function savePrediction(input: PredictionInput): Promise<void> {
  if (!supabase) return
  const { error } = await supabase.rpc('submit_prediction', {
    p_fight_id: input.fightId,
    p_winner_id: input.winnerId,
    p_method: input.method,
    p_round: input.round
  })
  if (error) throw error
}

export async function fetchLeaderboard(_period = 'all_time'): Promise<LeaderboardEntry[]> {
  if (!supabase) return demoLeaderboard
  const { data, error } = await supabase.rpc('get_leaderboard', { p_period: _period, p_limit: 100 })
  if (error) throw error
  return (data ?? []).map((row: any) => ({
    position: row.position,
    userId: row.user_id,
    username: row.username,
    displayName: row.display_name,
    avatarUrl: row.avatar_url,
    countryCode: row.country_code,
    points: row.points,
    perfectPicks: row.perfect_picks,
    winnersCorrect: row.winners_correct,
    scoredFights: row.scored_fights,
    trend: row.trend
  }))
}

export async function fetchProfile(username?: string): Promise<PublicProfileDTO> {
  if (!supabase) return demoProfile
  const { data, error } = await supabase.rpc('get_public_profile', { p_username: username ?? null })
  if (error) throw error
  return data as PublicProfileDTO
}

export async function fetchProfileHistory(username?: string): Promise<any[]> {
  if (!supabase) return demoProfile.recentEvents.map((event) => ({ ...event, predictions: demoPredictions.slice(0, 3).map((prediction, index) => ({
    ...prediction, redName: demoEvent.fights[index]?.red.name, blueName: demoEvent.fights[index]?.blue.name,
    predictedWinner: demoEvent.fights[index]?.red.name, predictedMethod: prediction.method, predictedRound: prediction.round,
    officialWinner: demoEvent.fights[index]?.red.name, officialMethod: prediction.method, officialRound: prediction.round, voided: false
  })) }))
  const { data, error } = await supabase.rpc('get_profile_history', { p_username: username ?? null })
  if (error) throw error
  return data ?? []
}

export async function fetchLeagues(): Promise<LeagueDTO[]> {
  if (!supabase) return demoLeagues
  const { data, error } = await supabase.from('my_leagues').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((row: any) => ({
    id: row.id,
    name: row.name,
    description: row.description,
    ownerId: row.owner_id,
    inviteCode: row.invite_code,
    membersCount: row.members_count,
    startsAt: row.starts_at,
    archivedAt: row.archived_at,
    userPosition: row.user_position,
    userPoints: row.user_points
  }))
}

export async function fetchLeagueLeaderboard(leagueId: string): Promise<LeaderboardEntry[]> {
  if (!supabase) return demoLeaderboard.slice(0, 5)
  const { data, error } = await supabase.rpc('get_league_leaderboard', { p_league_id: leagueId })
  if (error) throw error
  return (data ?? []).map((row: any) => ({
    position: row.position,
    userId: row.user_id,
    username: row.username,
    displayName: row.display_name,
    avatarUrl: row.avatar_url,
    countryCode: 'ZZ',
    points: row.points,
    perfectPicks: row.perfect_picks,
    winnersCorrect: row.winners_correct,
    scoredFights: row.scored_fights
  }))
}

export async function fetchChallenges(): Promise<ChallengeDTO[]> {
  if (!supabase) return demoChallenges
  const { data, error } = await supabase.from('my_challenges').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((row: any) => ({
    id: row.id,
    eventId: row.event_id,
    eventName: row.event_name,
    challenger: row.challenger,
    opponent: row.opponent,
    status: row.status,
    challengerPoints: row.challenger_points,
    opponentPoints: row.opponent_points,
    inviteCode: row.invite_code
  }))
}

export async function createLeague(input: { name: string; description?: string }) {
  if (!supabase) return demoLeagues[0]
  const { data, error } = await supabase.rpc('create_league', { p_name: input.name, p_description: input.description || null })
  if (error) throw error
  return data
}

export async function joinLeague(inviteCode: string) {
  if (!supabase) return demoLeagues[0]
  const { data, error } = await supabase.rpc('join_league', { p_invite_code: inviteCode })
  if (error) throw error
  return data
}

export async function createChallenge(input: { eventId: string; opponentUsername?: string }) {
  if (!supabase) return demoChallenges[0]
  const { data, error } = await supabase.rpc('create_challenge', { p_event_id: input.eventId, p_opponent_username: input.opponentUsername || null })
  if (error) throw error
  return data
}

export async function acceptChallenge(inviteCode: string) {
  if (!supabase) return demoChallenges[0]
  const { data, error } = await supabase.rpc('accept_challenge', { p_invite_code: inviteCode })
  if (error) throw error
  return data
}

export interface ProfileUpdateInput {
  displayName: string
  username: string
  bio?: string
  countryCode: string
  countryName: string
  coverTheme: 'coral' | 'indigo' | 'ember'
  socialLinks: Array<{ platform: string; url: string }>
}

export async function updateMyProfile(userId: string, input: ProfileUpdateInput) {
  if (!supabase) return
  if (!/^[a-z0-9_]{3,24}$/i.test(input.username.replace(/^@/, ''))) throw new Error('O username deve ter de 3 a 24 letras, números ou _.')
  const allowedHosts: Record<string, string[]> = {
    instagram: ['instagram.com'], x: ['x.com', 'twitter.com'], tiktok: ['tiktok.com'],
    youtube: ['youtube.com', 'youtu.be'], twitch: ['twitch.tv'], kick: ['kick.com']
  }
  for (const link of input.socialLinks) {
    const parsed = new URL(link.url)
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password || !allowedHosts[link.platform]?.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`))) {
      throw new Error(`O link de ${link.platform} não pertence ao domínio oficial.`)
    }
  }
  const { error } = await supabase.from('profiles').update({
    display_name: input.displayName,
    username: input.username.replace(/^@/, '').toLowerCase(),
    bio: input.bio || null,
    country_code: input.countryCode.toUpperCase(),
    country_name: input.countryName,
    cover_theme: input.coverTheme
  }).eq('auth_user_id', userId)
  if (error) throw error
  const { data: profile, error: profileError } = await supabase.from('profiles').select('id').eq('auth_user_id', userId).single()
  if (profileError) throw profileError
  const { error: deleteError } = await supabase.from('profile_social_links').delete().eq('profile_id', profile.id)
  if (deleteError) throw deleteError
  if (input.socialLinks.length) {
    const { error: linksError } = await supabase.from('profile_social_links').insert(input.socialLinks.map((link) => ({ profile_id: profile.id, platform: link.platform, url: link.url })))
    if (linksError) throw linksError
  }
}

async function cropAvatar(file: File) {
  const bitmap = await createImageBitmap(file)
  const side = Math.min(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 512
  const context = canvas.getContext('2d')!
  context.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, 512, 512)
  bitmap.close()
  const qualitySteps = [0.84, 0.72, 0.6, 0.5]
  for (const quality of qualitySteps) {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
    if (blob && blob.size <= 250_000) return blob
  }
  throw new Error('Não foi possível comprimir a imagem para 250 KB.')
}

export async function uploadMyAvatar(userId: string, file: File) {
  if (!supabase) return URL.createObjectURL(file)
  if (!file.type.startsWith('image/')) throw new Error('Escolha uma imagem válida.')
  const blob = await cropAvatar(file)
  const path = `${userId}/avatar.webp`
  const { error } = await supabase.storage.from('avatars').upload(path, blob, { contentType: 'image/webp', upsert: true, cacheControl: '3600' })
  if (error) throw error
  const { data } = supabase.storage.from('avatars').getPublicUrl(path)
  const versionedUrl = `${data.publicUrl}?v=${Date.now()}`
  const { error: profileError } = await supabase.from('profiles').update({ avatar_path: path, avatar_url: versionedUrl }).eq('auth_user_id', userId)
  if (profileError) throw profileError
  return versionedUrl
}

export async function exportMyAccount(userId: string) {
  if (!supabase) return { profile: demoProfile, predictions: demoPredictions, exportedAt: new Date().toISOString() }
  const [profile, socials, predictions, scores, leagues, challenges] = await Promise.all([
    supabase.from('profiles').select('*').eq('auth_user_id', userId).single(),
    supabase.from('profile_social_links').select('*').eq('profile_id', userId),
    supabase.from('predictions').select('*').eq('user_id', userId),
    supabase.from('event_scores').select('*').eq('user_id', userId),
    supabase.from('my_leagues').select('*'),
    supabase.from('my_challenges').select('*')
  ])
  for (const result of [profile, socials, predictions, scores, leagues, challenges]) if (result.error) throw result.error
  return { exportedAt: new Date().toISOString(), profile: profile.data, socialLinks: socials.data, predictions: predictions.data, eventScores: scores.data, leagues: leagues.data, challenges: challenges.data }
}

export async function deleteMyAccount() {
  if (!supabase) return
  const { error } = await supabase.rpc('delete_my_account')
  if (error) throw error
}

export interface AdminDashboardData {
  syncRuns: any[]
  conflicts: any[]
  profiles: any[]
  fighters: any[]
  audit: any[]
  counts: { profiles: number; leagues: number; challenges: number; pendingConflicts: number }
}

export async function fetchAdminDashboard(): Promise<AdminDashboardData> {
  if (!supabase) {
    const fighters = [...new Map(demoEvent.fights.flatMap((fight) => [fight.red, fight.blue]).map((fighter) => [fighter.id, fighter])).values()]
    return { syncRuns: [], conflicts: [], profiles: [{ id: demoProfile.id, username: demoProfile.username, display_name: demoProfile.displayName, is_suspended: false, created_at: demoProfile.createdAt }], fighters, audit: [], counts: { profiles: 1, leagues: demoLeagues.length, challenges: demoChallenges.length, pendingConflicts: 0 } }
  }
  const [syncRuns, conflicts, profiles, fighters, audit, profileCount, leagueCount, challengeCount] = await Promise.all([
    supabase.from('sync_runs').select('*').order('started_at', { ascending: false }).limit(12),
    supabase.from('sync_conflicts').select('*').eq('status', 'pending').order('created_at', { ascending: false }).limit(50),
    supabase.from('profiles').select('id,username,display_name,avatar_url,is_suspended,deleted_at,created_at').is('deleted_at', null).order('created_at', { ascending: false }).limit(50),
    supabase.from('fighters').select('*, weight_class:weight_classes(name)').is('deleted_at', null).order('display_name').limit(500),
    supabase.from('audit_log').select('*, actor:profiles(username,display_name)').order('created_at', { ascending: false }).limit(100),
    supabase.from('profiles').select('*', { count: 'exact', head: true }).is('deleted_at', null),
    supabase.from('leagues').select('*', { count: 'exact', head: true }).is('archived_at', null),
    supabase.from('challenges').select('*', { count: 'exact', head: true }).in('status', ['pending', 'accepted'])
  ])
  for (const result of [syncRuns, conflicts, profiles, fighters, audit, profileCount, leagueCount, challengeCount]) if (result.error) throw result.error
  return {
    syncRuns: syncRuns.data ?? [], conflicts: conflicts.data ?? [], profiles: profiles.data ?? [], fighters: fighters.data ?? [], audit: audit.data ?? [],
    counts: { profiles: profileCount.count ?? 0, leagues: leagueCount.count ?? 0, challenges: challengeCount.count ?? 0, pendingConflicts: conflicts.data?.length ?? 0 }
  }
}

export async function runUfcSync(mode: 'events' | 'rankings' | 'active' | 'fighters' = 'events') {
  if (!supabase) return { ok: true, mode, counts: {} }
  const { data, error } = await supabase.functions.invoke('sync-ufc', { body: { mode } })
  if (error) throw error
  if (!data?.ok) throw new Error(data?.error ?? 'A sincronização falhou.')
  return data
}

export async function saveFieldOverride(input: { entityType: string; entityId: string; fieldName: string; value: unknown; reason?: string }) {
  if (!supabase) return
  const { error } = await supabase.rpc('set_field_override', {
    p_entity_type: input.entityType, p_entity_id: input.entityId, p_field_name: input.fieldName,
    p_value: input.value, p_reason: input.reason || null
  })
  if (error) throw error
}

export async function resolveSyncConflict(input: { id: string; resolution: 'accepted_source' | 'kept_override' }) {
  if (!supabase) return
  const { error } = await supabase.rpc('resolve_sync_conflict', { p_conflict_id: input.id, p_resolution: input.resolution })
  if (error) throw error
}

export async function publishFightResult(input: { fightId: string; winnerId: string | null; method: string; round: number | null; time?: string }) {
  if (!supabase) return
  const { error } = await supabase.rpc('publish_fight_result', {
    p_fight_id: input.fightId, p_winner_id: input.winnerId, p_method: input.method,
    p_round: input.round, p_time: input.time || null, p_source_hash: null
  })
  if (error) throw error
}

export async function setAccountSuspension(input: { profileId: string; suspended: boolean; reason: string }) {
  if (!supabase) return
  const { error } = await supabase.rpc('set_account_suspension', { p_profile_id: input.profileId, p_suspended: input.suspended, p_reason: input.reason })
  if (error) throw error
}

export async function mergeFighters(input: { sourceId: string; targetId: string }) {
  if (!supabase) return
  const { error } = await supabase.rpc('merge_fighters', { p_source_id: input.sourceId, p_target_id: input.targetId })
  if (error) throw error
}

export async function createManualFighter(input: { displayName: string; slug: string; countryCode: string; countryName: string }) {
  if (!supabase) return
  const { error } = await supabase.from('fighters').insert({
    display_name: input.displayName, slug: input.slug, country_code: input.countryCode.toUpperCase(), country_name: input.countryName,
    source_data: { origin: 'manual' }
  })
  if (error) throw error
}

export async function createManualEvent(input: { name: string; headline: string; slug: string; startAt: string; city: string }) {
  if (!supabase) return
  const instant = new Date(input.startAt).toISOString()
  const { error } = await supabase.from('events').insert({
    name: input.name, headline: input.headline, slug: input.slug, start_at: instant, lock_at: instant,
    city: input.city, status: 'draft', source_data: { origin: 'manual' }
  })
  if (error) throw error
}

export async function createManualFight(input: { eventId: string; redFighterId: string; blueFighterId: string; weightClassName: string; segment: string; position: number; scheduledRounds: number; isTitleFight: boolean }) {
  if (!supabase) return
  const { error } = await supabase.from('fights').insert({
    event_id: input.eventId, red_fighter_id: input.redFighterId, blue_fighter_id: input.blueFighterId,
    weight_class_name: input.weightClassName, segment: input.segment, position: input.position,
    scheduled_rounds: input.scheduledRounds, is_title_fight: input.isTitleFight,
    source_data: { origin: 'manual' }
  })
  if (error) throw error
}
